import React, { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Select } from 'antd';
import PropTypes from 'prop-types';
import HoverHelp from '../../common/HoverHelp';

import { getIdMap } from '../../helpers/helpers';
import useDivisionFormTemplates from '../../common/hooks/useDivisionFormTemplates';

export default function ExportFormsConfig({
  canChangeTemplate,
  divisionId,
  formTemplateId,
  onFormTemplateSelected,
  dateFilterFieldId,
  onDateFilterSelected,
}) {
  const projects = useSelector((state) => state.projects.projects);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const divTemplates = useDivisionFormTemplates({ divisionId });
  const formTemplates = useSelector((state) => state.forms.templates);
  const dateFieldOptions = useMemo(() => {
    const base = [
      { value: 'createdAt', label: 'Created On' },
      { value: 'lastUpdated', label: 'Last Updated' },
    ];

    const tpl = formTemplates?.[formTemplateId];
    if (!tpl?.jsonFormData) return base;

    let data = {};
    try {
      data = JSON.parse(tpl.jsonFormData);
    } catch {
      data = {};
    }

    const dynamic = [];
    (data.sections || []).forEach((sec) => {
      (sec.fields ?? []).forEach((f) => {
        const t = (f.type ?? f.selectedType ?? '').toLowerCase();
        if (t === 'datetime') {
          dynamic.push({
            value: f.id,
            label: f.configProps?.title || f.label || f.name || f.id,
          });
        }
      });
    });

    return base.concat(dynamic);
  }, [formTemplateId, formTemplates]);
  const hasCustomDates = dateFieldOptions.length > 2;
  useEffect(() => {
    if (!dateFieldOptions.find((opt) => opt.value === dateFilterFieldId)) {
      onDateFilterSelected('lastUpdated');
    }
  }, [dateFieldOptions, dateFilterFieldId, onDateFilterSelected]);

  return (
    <Row align="middle" gutter={10} style={{ marginTop: 10, marginBottom: 20 }}>
      <Col>
        <Select
          placeholder="Select Form"
          style={{ width: 350 }}
          onSelect={onFormTemplateSelected}
          value={formTemplateId}
          optionFilterProp="label"
          showSearch
          dropdownMatchSelectWidth={false}
          disabled={!canChangeTemplate}
        >
          {divTemplates.map((template) => {
            const { projectId: templateProjectId } = template;
            let secondRow = null;
            if (templateProjectId && templateProjectId in projectIdMap) {
              const { [templateProjectId]: { name: projectName } = {} } = projectIdMap;
              secondRow = (
                <div style={{ fontSize: 10, fontStyle: 'italic', lineHeight: '12px' }}>
                  {projectName}
                </div>
              );
            }
            return (
              <Select.Option key={template.id} value={template.id} label={template.name}>
                {template.name}
                {secondRow}
              </Select.Option>
            );
          })}
        </Select>
      </Col>
      <Col>
        <Select
          style={{ width: 240 }}
          value={dateFilterFieldId}
          onChange={onDateFilterSelected}
          options={dateFieldOptions}
          placeholder="Date Filter"
          disabled={!hasCustomDates && dateFilterFieldId !== 'createdAt' && dateFilterFieldId !== 'lastUpdated'}
        />
      </Col>
      <Col>
        <HoverHelp
          content={
            <div style={{ maxWidth: 260 }}>
              This export filters by a custom date field.<br />
              If that field is deleted from the form,
              new submissions won’t appear in the export.<br />
              To fix it, edit the export and choose a new date field.
            </div>
          }
        />
      </Col>
    </Row>
  );
}

ExportFormsConfig.propTypes = {
  canChangeTemplate: PropTypes.bool.isRequired,
  divisionId: PropTypes.string,
  formTemplateId: PropTypes.string,
  onFormTemplateSelected: PropTypes.func.isRequired,
  dateFilterFieldId: PropTypes.string,
  onDateFilterSelected: PropTypes.func.isRequired,
};

ExportFormsConfig.defaultProps = {
  divisionId: null,
  formTemplateId: null,
  dateFilterFieldId: 'lastUpdated',
};
